import {
    createTextModel,
    type CoreMarker,
    type CoreTextModel,
    type XMLParseCallback
} from '../containers/xml-editing/services/common';
import type { LVGLSchemaService } from '../containers/xml-editing/services/LVGLSchemaService';
import { XMLValidatorCore } from '../containers/xml-editing/services/XMLValidatorCore';
import { hasXmlParseErrorElement } from './xmlParseError';

const PREVIEWABLE_XML_ROOT_TAGS: ReadonlySet<string> = new Set<string>(['component', 'widget', 'screen', 'test']);
const XML_ROOT_TAG_MATCHER: RegExp = /^<([A-Za-z_][\w.-]*)\b/;

export const INVALID_XML_FILE_MESSAGE: string = 'The currently opened XML file is invalid and cannot be previewed.';

const stripLeadingXmlDecorations = (xmlContent: string): string => {
    let content: string = xmlContent
        .trimStart()
        .replace(/^\uFEFF/, '')
        .trimStart();

    if (content.startsWith('<?xml')) {
        const declarationEnd: number = content.indexOf('?>');
        content = declarationEnd >= 0 ? content.slice(declarationEnd + 2).trimStart() : '';
    }

    while (content.startsWith('<!--')) {
        const commentEnd: number = content.indexOf('-->', 4);
        if (commentEnd === -1) return '';
        content = content.slice(commentEnd + 3).trimStart();
    }

    return content;
};

export const isLVGLPreviewableXml = (xmlContent: string): boolean => {
    if (!xmlContent) return false;

    const content: string = stripLeadingXmlDecorations(xmlContent);
    if (!content.startsWith('<')) return false;

    const tagMatch: RegExpMatchArray | null = content.match(XML_ROOT_TAG_MATCHER);
    if (!tagMatch) return false;

    return PREVIEWABLE_XML_ROOT_TAGS.has(tagMatch[1].toLowerCase());
};

export const hasXmlParseError = (xmlContent: string, parseXml: XMLParseCallback): boolean => {
    const document: Document = parseXml(xmlContent);
    return hasXmlParseErrorElement(document);
};

export const getXmlValidationMarkers = (
    coreModel: CoreTextModel,
    schemaService: LVGLSchemaService,
    parseXml: XMLParseCallback
): CoreMarker[] => XMLValidatorCore(coreModel, schemaService, parseXml);

export const getXmlContentValidationMarkers = (
    xmlContent: string,
    filePath: string,
    schemaService: LVGLSchemaService,
    parseXml: XMLParseCallback
): CoreMarker[] => getXmlValidationMarkers(createTextModel(xmlContent, filePath)(), schemaService, parseXml);
